import React, { FC, useState, useMemo } from 'react';
import { AnyFCO, FCOFields, FCOType, TransformationFCO, TransformationFCOFields } from '../../../core/types/fcoTypes';

import {
  ComboFilterCategory,
  ComboFilterDateSelector,
  ComboFilterHierarchicalList,
  ComboFilterHierarchyState,
  ComboFilterListSelector,
  ComboFilterRangeSelector,
  EmptyProps,
  ExtendedTableColumn,
  Link,
  Select,
  Table,
  TableViewLayout,
  Text,
  useTableFilters,
} from '@lithos';

import {
  isInBounds,
  isInDateRange,
  getFilteredItems,
  tableStringSortParameters,
  tableArrayLengthSortParameters,
  tableDateSortParameters,
} from '@lithos/TableUtils';

import { isEqual, startCase, uniq } from 'lodash';

import RelatedFCOsCell from '../fcoListView/RelatedFCOsCell';
import TagsCell from '../fcoListView/TagsCell';
import DateCell from '../fcoListView/DateCell';
import { areAnyCategoryMembersCurrentlyActive, countHierarchyActiveSelections } from '@lithos/ComboFilterUtils';
import { defaultDateState, defaultRangeState, defaultTagsState } from '../fcoListView/utils';
import TableColumnControls from '../fcoListView/TableColumnControls';
import TableSelectors from '../fcoListView/TableSelectors';

interface TransformationsListProps {
  items: TransformationFCO[];
  idToFcoMap: Record<string, AnyFCO>;
  workspace: string;
}

enum TransformationsListColumnKeys {
  NAME,
  TAGS,
  MODE,
  NUMBER_OF_FEATURE_VIEWS,
  FEATURE_VIEWS,
  NUMBER_OF_FEATURE_SERVICES,
  FEATURE_SERVICES,
  CREATED_BY,
  CREATED_AT,
  LAST_UPDATED_BY,
  LAST_UPDATED_AT,
}

interface TransformationsListFilterState {
  tags: ComboFilterHierarchyState;
  mode: string[];
  '# of Feature Views': [number, number];
  'Feature Views': string[];
  '# of Feature Services': [number, number];
  'Feature Services': string[];
  createdBy: string[];
  createdAt: [Date, Date];
  lastUpdatedBy: string[];
  lastUpdatedAt: [Date, Date];
}

const TransformationsList: FC<TransformationsListProps> = ({ items, idToFcoMap, workspace }) => {
  // Always need to set a default filter state
  const defaultFilterState: TransformationsListFilterState = useMemo(
    (): TransformationsListFilterState => ({
      tags: defaultTagsState(items),
      mode: [],
      '# of Feature Views': defaultRangeState(items, TransformationFCOFields.DEPENDENT_FEATURE_VIEWS),
      'Feature Views': [],
      '# of Feature Services': defaultRangeState(items, TransformationFCOFields.DEPENDENT_FEATURE_SERVICES),
      'Feature Services': [],
      createdBy: [],
      createdAt: defaultDateState(items, FCOFields.CREATED_DATE),
      lastUpdatedBy: [],
      lastUpdatedAt: defaultDateState(items, FCOFields.LAST_MODIFIED_DATE),
    }),
    [items]
  );

  const allValidOptions = useMemo(() => {
    return {
      Tags: Array.from(Object.keys(defaultTagsState(items))),
      mode: uniq(items.map((item) => item[TransformationFCOFields.MODE])),
      'Feature Views': uniq(items.map((item) => item[TransformationFCOFields.DEPENDENT_FEATURE_VIEWS]))
        .flat()
        .filter((source) => source !== undefined) as string[],
      'Feature Services': uniq(items.map((item) => item[TransformationFCOFields.DEPENDENT_FEATURE_SERVICES]))
        .flat()
        .filter((source) => source !== undefined) as string[],
      Owner: uniq(items.map((item) => item[FCOFields.OWNER] ?? '')).filter(
        (source) => source !== undefined
      ) as string[],
      'Last Updated By': uniq(items.map((item) => item[FCOFields.LAST_MODIFIED_BY] ?? '')).filter(
        (source) => source !== undefined
      ) as string[],
    };
  }, [items]);

  // Shared logic and states for filtered table views
  const {
    searchQuery,
    hasAnyFilters,
    activeFilterCount,
    sortType,
    setSortType,
    activeHeading,
    setActiveHeading,
    filterState,
    onSearchInputChange,
    handleResetFilters,
    handleFilterChange,
  } = useTableFilters<TransformationFCO, TransformationsListColumnKeys, TransformationsListFilterState>(
    defaultFilterState
  );

  // Filter components are the actual input components (e.g. A combo filter listt)
  // These are defined separately from the ComboFilters because they are reused in the table header
  // They may also be reused in shortcut popovers in the general filter list
  // This allows us to reference the same component in multiple places
  const filterComponents: Record<
    TransformationsListColumnKeys,
    {
      filter: React.ReactNode;
      activeCount: () => number;
      filterCallback: (source: TransformationFCO) => boolean;
      name: string;
    }
  > = useMemo(() => {
    return {
      [TransformationsListColumnKeys.NAME]: {
        filter: <></>,
        activeCount: () => 0,
        filterCallback: () => true,
        name: 'Name',
      },
      [TransformationsListColumnKeys.TAGS]: {
        filter: (
          <ComboFilterHierarchicalList
            items={filterState.tags}
            setItems={(tags) => {
              handleFilterChange(filterComponents, 'tags', tags);
            }}
          />
        ),
        activeCount: () => countHierarchyActiveSelections(filterState.tags),
        filterCallback: (source) => {
          let hasEachCategoryMatched = true;
          const categories = Object.keys(filterState.tags);
          categories.forEach((category) => {
            if (areAnyCategoryMembersCurrentlyActive(filterState.tags[category])) {
              const activeSubcategories = Object.entries(filterState.tags[category])
                .filter((entry) => entry[1])
                .map((result) => result[0]);
              const hasMatch = source.tags.find(
                (tag) => tag.key === category && activeSubcategories.includes(tag.value)
              );
              if (hasMatch === undefined) {
                hasEachCategoryMatched = false;
              }
            }
          });

          return hasEachCategoryMatched;
        },
        name: 'Tags',
      },
      [TransformationsListColumnKeys.MODE]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.mode}
            setSelected={(mode) => handleFilterChange(filterComponents, 'mode', mode)}
            items={uniq(items.map((item) => item[TransformationFCOFields.MODE] ?? '')).sort()}
            title={''}
          />
        ),
        activeCount: () => filterState.mode.length,
        filterCallback: (transformation) => {
          return filterState.mode.includes(transformation[TransformationFCOFields.MODE] ?? '');
        },
        name: 'Mode',
      },
      [TransformationsListColumnKeys.NUMBER_OF_FEATURE_VIEWS]: {
        filter: (
          <ComboFilterRangeSelector
            value={filterState['# of Feature Views']}
            setValue={(range) => {
              handleFilterChange(filterComponents, '# of Feature Views', range);
            }}
            bounds={defaultFilterState['# of Feature Views']}
          />
        ),
        activeCount: () =>
          isEqual(filterState['# of Feature Views'], defaultFilterState['# of Feature Views']) ? 0 : 1,
        filterCallback: (source) => {
          return isInBounds(
            source[TransformationFCOFields.DEPENDENT_FEATURE_VIEWS].length,
            filterState['# of Feature Views']
          );
        },
        name: '# of Feature Views',
      },
      [TransformationsListColumnKeys.FEATURE_VIEWS]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['Feature Views']}
            setSelected={(type) => handleFilterChange(filterComponents, 'Feature Views', type)}
            icon={'FeatureView'}
            items={uniq(
              items
                .map((item) => item[TransformationFCOFields.DEPENDENT_FEATURE_VIEWS])
                .flat()
                .filter((source) => source !== undefined)
                .map((id) => idToFcoMap[id][FCOFields.NAME])
            )
              .sort()
              .map(startCase)}
            title={''}
          />
        ),
        activeCount: () => filterState['Feature Views'].length,
        filterCallback: (source) => {
          const featureViewNames = source[TransformationFCOFields.DEPENDENT_FEATURE_VIEWS].map(
            (featureViewId) => idToFcoMap[featureViewId]
          );
          return (
            filterState['Feature Views'].length === 0 ||
            featureViewNames.some((fv) => filterState['Feature Views'].includes(startCase(fv[FCOFields.NAME])))
          );
        },
        name: 'Feature Views',
      },
      [TransformationsListColumnKeys.NUMBER_OF_FEATURE_SERVICES]: {
        filter: (
          <ComboFilterRangeSelector
            value={filterState['# of Feature Services']}
            setValue={(range) => {
              handleFilterChange(filterComponents, '# of Feature Services', range);
            }}
            bounds={defaultFilterState['# of Feature Services']}
          />
        ),
        activeCount: () =>
          isEqual(filterState['# of Feature Services'], defaultFilterState['# of Feature Services']) ? 0 : 1,
        filterCallback: (source) => {
          return isInBounds(
            source[TransformationFCOFields.DEPENDENT_FEATURE_SERVICES].length,
            filterState['# of Feature Services']
          );
        },
        name: '# of Feature Services',
      },
      [TransformationsListColumnKeys.FEATURE_SERVICES]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['Feature Services']}
            setSelected={(type) => handleFilterChange(filterComponents, 'Feature Services', type)}
            icon="FeatureService"
            items={uniq(
              items
                .map((item) => item[TransformationFCOFields.DEPENDENT_FEATURE_SERVICES])
                .flat()
                .filter((source) => source !== undefined)
                .map((id) => idToFcoMap[id][FCOFields.NAME])
            )
              .sort()
              .map(startCase)}
            title={''}
          />
        ),
        activeCount: () => filterState['Feature Services'].length,
        filterCallback: (source) => {
          const featureServiceNames = source[TransformationFCOFields.DEPENDENT_FEATURE_SERVICES]
            .map((featureServiceId) => idToFcoMap[featureServiceId])
            .map((featureService) => featureService.name);

          return (
            filterState['Feature Services'].length === 0 ||
            featureServiceNames.some((featureService) =>
              filterState['Feature Services'].includes(startCase(featureService))
            )
          );
        },
        name: 'Feature Services',
      },
      [TransformationsListColumnKeys.LAST_UPDATED_BY]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.lastUpdatedBy}
            setSelected={(type) => handleFilterChange(filterComponents, 'lastUpdatedBy', type)}
            items={uniq(
              items.map((item) => item[FCOFields.LAST_MODIFIED_BY] ?? '').filter((source) => source !== undefined)
            ).sort()}
            title={''}
          />
        ),
        activeCount: () => filterState.lastUpdatedBy.length,
        filterCallback: (source) => {
          return filterState.lastUpdatedBy.includes(source[FCOFields.LAST_MODIFIED_BY] ?? '');
        },
        name: 'Last Updated By',
      },
      [TransformationsListColumnKeys.LAST_UPDATED_AT]: {
        filter: (
          <ComboFilterDateSelector
            start={filterState.lastUpdatedAt[0]}
            setStart={(date) => {
              handleFilterChange(filterComponents, 'lastUpdatedAt', [date as Date, filterState.lastUpdatedAt[1]]);
            }}
            end={filterState.lastUpdatedAt[1]}
            setEnd={(date) => {
              handleFilterChange(filterComponents, 'lastUpdatedAt', [filterState.lastUpdatedAt[0], date as Date]);
            }}
          />
        ),
        activeCount: () => (isEqual(filterState.lastUpdatedAt, defaultFilterState.lastUpdatedAt) ? 0 : 1),
        filterCallback: (source) => {
          return isInDateRange(source[FCOFields.LAST_MODIFIED_DATE], filterState.lastUpdatedAt);
        },
        name: 'Last Updated At',
      },
      [TransformationsListColumnKeys.CREATED_BY]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.createdBy}
            setSelected={(type) => handleFilterChange(filterComponents, 'createdBy', type)}
            items={uniq(
              items.map((item) => item[FCOFields.OWNER] ?? '').filter((source) => source !== undefined)
            ).sort()}
            title={''}
          />
        ),
        activeCount: () => filterState.createdBy.length,
        filterCallback: (source) => {
          return filterState.createdBy.includes(source[FCOFields.OWNER] ?? '');
        },
        name: 'Owner',
      },
      [TransformationsListColumnKeys.CREATED_AT]: {
        filter: (
          <ComboFilterDateSelector
            start={filterState.createdAt[0]}
            setStart={(date) => {
              handleFilterChange(filterComponents, 'createdAt', [date as Date, filterState.createdAt[1]]);
            }}
            end={filterState.lastUpdatedAt[1]}
            setEnd={(date) => {
              handleFilterChange(filterComponents, 'createdAt', [filterState.createdAt[0], date as Date]);
            }}
          />
        ),
        activeCount: () => (isEqual(filterState.createdAt, defaultFilterState.createdAt) ? 0 : 1),
        filterCallback: (source) => {
          return isInDateRange(source[FCOFields.CREATED_DATE], filterState.createdAt);
        },
        name: 'Created At',
      },
    };
  }, [filterState, items, idToFcoMap, defaultFilterState, handleFilterChange]);

  // These define the filter categories in the Filter button popover
  const categories: ComboFilterCategory[] = [
    {
      name: 'Tags',
      icon: 'Tag',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'tags', defaultFilterState.tags);
      },
      activeCount: countHierarchyActiveSelections(filterState.tags),
      popover: filterComponents[TransformationsListColumnKeys.TAGS].filter,
      disabled: allValidOptions['Tags'].length === 0,
    },
    {
      name: 'Mode',
      icon: 'Transformations',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'mode', defaultFilterState.mode);
      },
      activeCount: filterState.mode.length,
      popover: filterComponents[TransformationsListColumnKeys.MODE].filter,
    },
    {
      name: '# of Feature Views',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, '# of Feature Views', defaultFilterState['# of Feature Views']);
      },
      activeCount: isEqual(filterState['# of Feature Views'], defaultFilterState['# of Feature Views']) ? 0 : 1,
      popover: filterComponents[TransformationsListColumnKeys.NUMBER_OF_FEATURE_VIEWS].filter,
    },
    {
      name: 'Feature Views',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Feature Views', defaultFilterState['Feature Views']);
      },
      activeCount: filterState['Feature Views'].length,
      popover: filterComponents[TransformationsListColumnKeys.FEATURE_VIEWS].filter,
      disabled: allValidOptions['Feature Views'].length === 0,
    },
    {
      name: '# of Feature Services',
      icon: 'FeatureService',
      resetCallback: () => {
        handleFilterChange(filterComponents, '# of Feature Services', defaultFilterState['# of Feature Services']);
      },
      activeCount: isEqual(filterState['# of Feature Services'], defaultFilterState['# of Feature Services']) ? 0 : 1,
      popover: filterComponents[TransformationsListColumnKeys.NUMBER_OF_FEATURE_SERVICES].filter,
    },
    {
      name: 'Feature Services',
      icon: 'FeatureService',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Feature Services', defaultFilterState['Feature Services']);
      },
      activeCount: filterState['Feature Services'].length,
      popover: filterComponents[TransformationsListColumnKeys.FEATURE_SERVICES].filter,
      disabled: allValidOptions['Feature Services'].length === 0,
    },
    {
      name: 'Owner',
      icon: 'UserCircle',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'createdBy', defaultFilterState.createdBy);
      },
      activeCount: filterState.createdBy.length,
      popover: filterComponents[TransformationsListColumnKeys.CREATED_BY].filter,
      disabled: allValidOptions['Owner'].length === 0,
    },
    {
      name: 'Created At',
      icon: 'Calendar',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'createdAt', defaultFilterState.createdAt);
      },
      activeCount: isEqual(filterState.createdAt, defaultFilterState.createdAt) ? 0 : 1,
      popover: filterComponents[TransformationsListColumnKeys.CREATED_AT].filter,
    },
    {
      name: 'Last Updated By',
      icon: 'UserCircle',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'lastUpdatedBy', defaultFilterState.lastUpdatedBy);
      },
      activeCount: filterState.lastUpdatedBy.length,
      popover: filterComponents[TransformationsListColumnKeys.LAST_UPDATED_BY].filter,
      disabled: allValidOptions['Last Updated By'].length === 0,
    },
    {
      name: 'Last Updated At',
      icon: 'Calendar',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'lastUpdatedAt', defaultFilterState.lastUpdatedAt);
      },
      activeCount: isEqual(filterState.lastUpdatedAt, defaultFilterState.lastUpdatedAt) ? 0 : 1,
      popover: filterComponents[TransformationsListColumnKeys.LAST_UPDATED_AT].filter,
    },
  ];

  // Define the columns for the table
  // The sortCallback is confusing and needs to be simplified
  const columns: ExtendedTableColumn<TransformationFCO>[] = [
    {
      name: 'Name',
      id: TransformationsListColumnKeys.NAME,
      content: (transformation) => (
        <Link
          name={transformation[FCOFields.NAME]}
          icon="Sources"
          url={`/repo/${workspace}/transformations/${transformation[FCOFields.NAME]}/overview`}
          testId={`transformations-link-${transformation[FCOFields.NAME]}`}
        />
      ),
      columnWidth: 'l',
      ...tableStringSortParameters(sortType, FCOFields.NAME, setSortType),
    },
    {
      name: 'Tags',
      id: TransformationsListColumnKeys.TAGS,
      content: (transformation) => <TagsCell fco={transformation} />,
      filterContent: filterComponents[TransformationsListColumnKeys.TAGS].filter,
      ...tableArrayLengthSortParameters(sortType, FCOFields.TAGS, setSortType),
    },
    {
      name: 'Mode',
      id: TransformationsListColumnKeys.MODE,
      content: (transformation) => <Text>{transformation[TransformationFCOFields.MODE] ?? '-'}</Text>,
      filterContent: filterComponents[TransformationsListColumnKeys.MODE].filter,
      ...tableStringSortParameters(sortType, TransformationFCOFields.MODE, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Feature Views',
      id: TransformationsListColumnKeys.FEATURE_VIEWS,
      content: (source) => {
        const relatedFeatureViews = source[TransformationFCOFields.DEPENDENT_FEATURE_VIEWS].map((id) => idToFcoMap[id]);
        return <RelatedFCOsCell workspace={workspace} fcos={relatedFeatureViews} type={FCOType.FEATURE_VIEW} />;
      },
      filterContent: filterComponents[TransformationsListColumnKeys.FEATURE_VIEWS].filter,
      ...tableArrayLengthSortParameters(sortType, TransformationFCOFields.DEPENDENT_FEATURE_VIEWS, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Feature Services',
      id: TransformationsListColumnKeys.FEATURE_SERVICES,
      content: (source) => {
        const relatedFeatureServices = source[TransformationFCOFields.DEPENDENT_FEATURE_SERVICES].map(
          (id) => idToFcoMap[id]
        );
        return <RelatedFCOsCell workspace={workspace} fcos={relatedFeatureServices} type={FCOType.FEATURE_SERVICE} />;
      },
      filterContent: filterComponents[TransformationsListColumnKeys.FEATURE_SERVICES].filter,
      ...tableArrayLengthSortParameters(sortType, TransformationFCOFields.DEPENDENT_FEATURE_SERVICES, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Last Updated By',
      id: TransformationsListColumnKeys.LAST_UPDATED_BY,
      content: (source) => (
        <Link avatar={{ name: source[FCOFields.LAST_MODIFIED_BY] }} name={source[FCOFields.LAST_MODIFIED_BY]} noHover />
      ),
      filterContent: filterComponents[TransformationsListColumnKeys.LAST_UPDATED_BY].filter,
      columnWidth: 'm',
      ...tableStringSortParameters(sortType, FCOFields.LAST_MODIFIED_BY, setSortType),
    },
    {
      name: 'Last Updated At',
      id: TransformationsListColumnKeys.LAST_UPDATED_AT,
      content: (source) => <DateCell date={source[FCOFields.LAST_MODIFIED_DATE]} />,
      filterContent: filterComponents[TransformationsListColumnKeys.LAST_UPDATED_AT].filter,
      ...tableDateSortParameters(sortType, FCOFields.LAST_MODIFIED_DATE, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Owner',
      id: TransformationsListColumnKeys.CREATED_BY,
      content: (source) => <Link noHover avatar={{ name: source[FCOFields.OWNER] }} name={source[FCOFields.OWNER]} />,
      filterContent: filterComponents[TransformationsListColumnKeys.CREATED_BY].filter,
      ...tableStringSortParameters(sortType, FCOFields.OWNER, setSortType),
    },
    {
      name: 'Created At',
      id: TransformationsListColumnKeys.CREATED_AT,
      content: (source) => <DateCell date={source[FCOFields.CREATED_DATE]} />,
      filterContent: filterComponents[TransformationsListColumnKeys.CREATED_AT].filter,
      ...tableDateSortParameters(sortType, FCOFields.CREATED_DATE, setSortType),
      columnWidth: 'm',
    },
  ];

  const selectableColumns = columns.map((column) => {
    return { name: column.name as string, visible: true };
  });

  const [columnState, setColumnState] = useState<{ name: string; visible: boolean }[]>(selectableColumns);

  // useEffect(() => {
  //   handleFilterChange(filterComponents);
  // }, [filterState, filterComponents, handleFilterChange]);

  const columnsSelector = (
    <Select
      groups={[
        {
          items: columnState.map((column) => {
            return {
              name: column.name,
              asToggle: true,
              onClick: () => {
                const newColumnState = columnState.map((selectableColumn) => {
                  if (selectableColumn.name !== column.name) {
                    return selectableColumn;
                  }

                  return {
                    name: selectableColumn.name,
                    visible: !selectableColumn.visible,
                  };
                });
                setColumnState && setColumnState(newColumnState);
              },
              selected: column.visible,
              selectable: true,
            };
          }),
        },
      ]}
      filterable={false}
    />
  );

  const filterEmpty: EmptyProps = {
    actions: <></>,
    placement: 'full-page',
    useCase: 'empty',
    title: 'No Results',
    description: 'No Sources match the current filters. Try adjusting your filters or search query.',
  };

  const filteredColumns = columns
    .map((column) => {
      const matching = columnState.find((stateColumn) => stateColumn.name === column.name);
      if (!matching) {
        return;
      }
      if (matching?.visible !== true) {
        return;
      }

      const asColumn: ExtendedTableColumn<TransformationFCO> = {
        ...column,
        isActive: column.id === activeHeading,
        setIsActive: (arg) => {
          if (arg) {
            setActiveHeading(column.id as TransformationsListColumnKeys);
            return;
          }
          setActiveHeading(undefined);
        },
        hideCallback: () => {
          const newColumnState = columnState.map((selectableColumn) => {
            if (selectableColumn.name !== column.name) {
              return selectableColumn;
            }

            return {
              name: selectableColumn.name,
              visible: !selectableColumn.visible,
            };
          });

          setColumnState && setColumnState(newColumnState);
        },
        columnsContent: columnsSelector,
      };

      return asColumn;
    })
    .filter((column) => column !== undefined) as ExtendedTableColumn<TransformationFCO>[];

  const activeFilters = Object.values(filterComponents).filter((filter) => {
    return filter.activeCount() !== 0;
  });

  const filteredResults = useMemo(() => {
    return getFilteredItems(items, activeFilters, [FCOFields.NAME, FCOFields.ID], searchQuery ?? '', sortType);
  }, [activeFilters, items, searchQuery, sortType]);

  return (
    <TableViewLayout
      searchInput={
        <TableSelectors
          hasAnyFilters={hasAnyFilters}
          activeFilterCount={activeFilterCount}
          handleResetFilters={handleResetFilters}
          categories={categories}
          onSearchInputChange={onSearchInputChange}
        />
      }
      filterAndColumnControls={<TableColumnControls columnsSelector={columnsSelector} />}
      table={
        <>
          <Table
            items={filteredResults}
            columns={filteredColumns}
            frozenFirstColumn={true}
            emptyProps={filterEmpty}
            memoized={true}
          />
        </>
      }
    />
  );
};

export default TransformationsList;
